import * as React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as LocksState from "../../store/Locks";
import { PageControl } from "./PageControl";
import { debounce } from "lodash";
import { defaultPageSize } from "../../helpers";
import { LockResponse } from "../../api/ApiClient";
import { Spinner } from "./Spinner";
import LocksFilters from "components/Locks/Filters";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";

interface Injected {
  locks: LocksState.LocksState;
}

type OwnProps = {
  onSelected: (lock: LockResponse) => any;
  selectedLocks?: Array<LockResponse>;
  pageSize?: number;
  showFirst?: number | null | undefined;
  inProgressLocks?: Array<LockResponse>;
  disabledLocks?: Array<LockResponse>;
  renderButton?: (lock: LockResponse) => any;
  inProgressLabel?: string;
  selectedLabel?: string;
  unselectedLabel?: string;
  selectedBtnClass?: string;
  unselectedBtnClass?: string;
  btnStyle?: Object;
};

interface SnackProps {
  enqueueSnackbar?: (node: React.ReactNode, options?: any) => void;
  closeSnackbar?: (key?: any) => void;
}

type Props = Injected &
  typeof LocksState.actionCreators &
  OwnProps &
  SnackProps;

interface State {
  typedLock: string;
  siteId: string;
  doorType: string;
}

const LockIndicator = (lock: LockResponse) => {
  let badgeClass = "badge";
  let badgeText = "online";
  if (lock.lastSeenTimeStamp) {
    let now = new Date().getTime();
    let lockLastSeen = new Date(lock.lastSeenTimeStamp + "Z").getTime();
    let difference = (now - lockLastSeen) / 1000 / 60;
    if (difference <= 5) {
      badgeClass += " alert-success";
    } else if (difference <= 20) {
      badgeClass += " alert-warning";
    } else {
      badgeClass += " alert-danger";
      badgeText = "offline";
    }
  }
  return (
    <span>
      {lock.name}{" "}
      {lock.lastSeenTimeStamp && (
        <span
          className={badgeClass}
          data-toggle="tooltip"
          title={
            "Last Seen: " +
            new Date(lock.lastSeenTimeStamp + "Z").toLocaleString()
          }
        >
          {badgeText}
        </span>
      )}{" "}
    </span>
  );
};

class SelectLock extends React.Component<Props, State> {
  state = {
    typedLock: "",
    siteId: "",
    doorType: "",
  };

  componentDidMount() {
    console.log("SelectLock: Requesting locks...");
    this.refresh();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.pageSize !== prevProps.pageSize) {
      this.refresh();
    }
  }

  refresh = async () => {
    try {
      const { pageSize } = this.props;
      const { typedLock: searchTerm, doorType, siteId } = this.state;
      await this.props.setFetchParamsLocks({
        page: 0,
        pageSize: pageSize || defaultPageSize,
        searchTerm,
        doorType,
        siteId,
      });
    } catch (e) {
      console.error("Error requesting locks", e);
      const message = (e as any)?.message || "Error requesting locks";
      this.props.enqueueSnackbar &&
        this.props.enqueueSnackbar(
          <span style={{ fontSize: 12 }}>{message}</span>,
          { variant: "error" },
        );
    }
  };

  handleTypedLock = (value: string) => {
    this.setState({ typedLock: value }, this.searchLock);
  };

  handleSiteId = (value: string) => {
    this.setState({ siteId: value }, this.searchLock);
  };

  handleDoorType = (value: string) => {
    this.setState({ doorType: value }, this.searchLock);
  };

  resetFilters = () => {
    this.setState({ typedLock: "", siteId: "", doorType: "" }, this.searchLock);
  };

  searchLock = debounce(this.refresh, 1000);

  public render() {
    const { locks, isLoading } = this.props.locks;

    return (
      <div>
        <LocksFilters
          {...this.state}
          handleTypedLock={this.handleTypedLock}
          handleSiteId={this.handleSiteId}
          handleDoorType={this.handleDoorType}
          resetFilters={this.resetFilters}
        />
        <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isLoading && <Spinner />}
        </h4>
        {this.renderLocksTable(locks)}
      </div>
    );
  }

  private defaultRenderButton = (lock: LockResponse) => {
    const {
      selectedLocks,
      inProgressLocks,
      disabledLocks,
      onSelected,
      inProgressLabel,
      selectedLabel,
      unselectedLabel,
      selectedBtnClass,
      unselectedBtnClass,
      btnStyle,
    } = this.props;

    const isSelected =
      selectedLocks && selectedLocks.find((l) => l.id === lock.id);

    const isInProgress =
      inProgressLocks && inProgressLocks.find((l) => l.id === lock.id);

    const isDisabledLock =
      disabledLocks && disabledLocks.find((l) => l.id === lock.id);

    const className = isSelected
      ? selectedBtnClass || "btn-primary"
      : unselectedBtnClass || "btn-default";
    const disabled = !!isInProgress || !!isDisabledLock;

    return (
      <Button
        className={className}
        disabled={disabled}
        onClick={(_: any) => onSelected(lock)}
        style={btnStyle as React.CSSProperties | undefined}
        type="button"
        variant={isSelected ? "default" : "outline"}
      >
        {isInProgress
          ? inProgressLabel || "Opening..."
          : isSelected
            ? selectedLabel || "Select"
            : unselectedLabel || "Unselect"}
      </Button>
    );
  };

  private renderLocksTable(locks: LockResponse[]) {
    const { renderButton, showFirst } = this.props;
    const { isLoading } = this.props.locks;
    locks = !showFirst ? locks : (locks || []).slice(0, showFirst);

    return (
      <div>
        <div className="mb-4 overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
          <Table
            className="[&_tbody>tr:nth-of-type(odd)]:bg-slate-50 [&_th]:whitespace-normal [&_th]:text-xs [&_td]:whitespace-normal [&_td]:text-xs"
          >
            <TableHeader>
              <TableRow>
                <TableHead>Lock Name</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Door Type</TableHead>
                <TableHead>SystemLockId</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {locks.map((lock, i) => (
                <TableRow key={"f_" + i}>
                  <TableCell>
                    <LockIndicator {...lock} />
                  </TableCell>
                  <TableCell>
                    {lock && lock.site ? lock.site.name : ""}
                  </TableCell>
                  <TableCell>{lock.doorType}</TableCell>
                  <TableCell>{lock.systemLockId}</TableCell>
                  <TableCell>
                    {renderButton
                      ? renderButton(lock)
                      : this.defaultRenderButton(lock)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!showFirst && (
          <PageControl
            onNext={this.props.nextLockPage}
            onPrev={this.props.prevLockPage}
            setPageSize={this.props.setPageSize}
            pageSize={this.props.pageSize || defaultPageSize}
            currentPage={this.props.locks.page}
            isLoading={isLoading}
            disablePrev={this.props.locks.page === 0}
            disableNext={
              this.props.locks.locks.length < this.props.locks.pageSize
            }
          />
        )}
      </div>
    );
  }
}

const ConnectedSelectLock = connect<any, any, OwnProps, ApplicationState>(
  (state) => ({
    locks: state.locks,
  }),
  { ...LocksState.actionCreators },
)(SelectLock);

const SelectLockWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedSelectLock
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default SelectLockWithSnackbar;
